/**
 * boardText — the words and the ping rows the Practice Ops board draws.
 *
 * WHY THIS EXISTS (2026-10-30, plan "Design review" → Patient card spec, Alerts column). board.js turns the
 * Firestore document into cards; this file turns a card into what is WRITTEN on it ("Waiting 14 min",
 * "Ready 9:12", "Late 12 min", "Checkout 9:38") and turns the document's `alerts[]` into the Alerts column's
 * ping rows. Both are pure, so the wording is pinned by a node-test and the components stay thin.
 *
 * CLOCK TEXT. Every time on the board is a wall-clock time at the PRACTICE, not in the viewer's zone: a
 * front-desk laptop set to another zone must still read "9:15" for a 9:15 appointment. `tz` is the site's
 * GENERAL_TIMEZONE (an IANA name); missing or unknown falls back to the viewer's zone. The design prints
 * 12-hour times WITHOUT "am/pm" ("9:46"), and so does this.
 *
 * TIMER TEXT (one per card, `timerText`):
 *   Check-in   "Early · appt 9:15" while the patient arrived before the booked time and it is not yet that
 *              time; else "Waiting N min" (card.waitMin, already computed from timestamps by board.js).
 *   room       "In chair N min"
 *   Check-out  "Checkout 9:38", or "Dismissed 9:05" for a dismissed patient
 *   A card with nothing to say (no times known) gives '' and draws no chip.
 *
 * TAGS (`cardTags`, Check-in cards only, none at size `sm`): "Ready 9:12" and "Late 12 min" (arrived after the
 * booked time). "Early" is the timer text above, not a tag.
 *
 * PINGS (`buildPings`). The document's `alerts[]` → one row per ping: the recipient's colour on the left edge
 * and tinted background, an elapsed chip ("Just sent" / "Waiting 6 min" / "Waiting 1 hr 5 min") whose tone follows
 * the SAME wait threshold as the cards (amber at 1x, red at 2x; off when the office's threshold is off), and a
 * legend of recipients. The legend shows when there are 2+ recipients, or ONE recipient with 3+ pings. `index` is
 * the ping's position in the ORIGINAL array: the classic page deletes by that index (_deleteAlert).
 *
 * ⚠️ Keep this file import-free. The node-tests execute this exact file. No clock is read here: `nowMs` is an
 * argument.
 */

const MS_PER_MINUTE = 60 * 1000;

function isFiniteNumber(v) {
  return typeof v === 'number' && Number.isFinite(v);
}

function text(v) {
  return typeof v === 'string' ? v.trim() : '';
}

function partsOf(ms, tz, options) {
  if (!isFiniteNumber(ms)) return null;
  const date = new Date(ms);
  if (isNaN(date.getTime())) return null;
  const run = (zone) => {
    const opts = Object.assign({}, options);
    if (zone) opts.timeZone = zone;
    return new Intl.DateTimeFormat('en-US', opts).formatToParts(date);
  };
  try {
    return run(typeof tz === 'string' && tz.trim() !== '' ? tz.trim() : null);
  } catch (e) {
    try { return run(null); } catch (err) { return null; }
  }
}

function part(parts, type) {
  const found = parts.find((p) => p.type === type);
  return found ? found.value : '';
}

/** '9:46' for an instant at the practice (12-hour, no am/pm); '' for a missing or invalid time. */
export function clockText(ms, tz) {
  const parts = partsOf(ms, tz, { hour: 'numeric', minute: '2-digit', hour12: true });
  if (!parts) return '';
  const hour = Number(part(parts, 'hour'));
  const minute = part(parts, 'minute');
  if (!Number.isFinite(hour) || !minute) return '';
  return `${hour === 0 ? 12 : hour}:${minute}`;
}

/** 'Friday, October 30' — the board's date line, at the practice. '' for a missing time. */
export function dateLine(ms, tz) {
  const parts = partsOf(ms, tz, { weekday: 'long', month: 'long', day: 'numeric' });
  if (!parts) return '';
  const weekday = part(parts, 'weekday');
  const month = part(parts, 'month');
  const day = part(parts, 'day');
  return weekday && month && day ? `${weekday}, ${month} ${day}` : '';
}

/** 'YYYY-MM-DD HH:mm:ss' at the practice — the shape Open Dental's AptDateTime has. '' when unusable. */
export function localDateTimeText(ms, tz) {
  const parts = partsOf(ms, tz, {
    year: 'numeric', month: '2-digit', day: '2-digit', hour: '2-digit', minute: '2-digit', second: '2-digit', hourCycle: 'h23',
  });
  if (!parts) return '';
  const hour = part(parts, 'hour') === '24' ? '00' : part(parts, 'hour');
  return `${part(parts, 'year')}-${part(parts, 'month')}-${part(parts, 'day')} ${hour}:${part(parts, 'minute')}:${part(parts, 'second')}`;
}

/** The chip text on a card; '' when there is nothing to say. See the header. */
export function timerText(card, tz) {
  const c = card && typeof card === 'object' ? card : {};
  if (c.column === 'checkout') {
    if (c.dismissed) {
      const at = clockText(c.dismissedMs, tz);
      return at ? `Dismissed ${at}` : 'Dismissed';
    }
    const at = clockText(c.checkoutMs, tz);
    return at ? `Checkout ${at}` : 'Checked out';
  }
  if (c.column === 'room') {
    return isFiniteNumber(c.inChairMin) ? `In chair ${c.inChairMin} min` : 'In chair';
  }
  // Check-in (the default column)
  if (c.early) {
    const at = clockText(c.scheduledMs, tz);
    return at ? `Early · appt ${at}` : 'Early';
  }
  return isFiniteNumber(c.waitMin) ? `Waiting ${c.waitMin} min` : '';
}

/** [{ key, text }] — "Ready 9:12", "Late 12 min". Check-in cards only; none at size 'sm'. */
export function cardTags(card, tz, size) {
  const c = card && typeof card === 'object' ? card : {};
  if (size === 'sm' || c.column !== 'checkin') return [];
  const tags = [];
  if (c.status === 'ready') {
    const at = clockText(c.readyMs, tz);
    tags.push({ key: 'ready', text: at ? `Ready ${at}` : 'Ready' });
  }
  if (isFiniteNumber(c.lateMin) && c.lateMin > 0) tags.push({ key: 'late', text: `Late ${c.lateMin} min` });
  return tags;
}

/**
 * The line under a column title that counts the waiting: { a: '3 waiting', b: ' · 2 over 15 min' }.
 * `a` is '' when nobody has a live wait; `b` is '' when nobody is over (or the threshold is off).
 */
export function waitingLine(counts, thresholdMin) {
  const c = counts && typeof counts === 'object' ? counts : {};
  const waiting = isFiniteNumber(c.waiting) ? c.waiting : 0;
  const over = isFiniteNumber(c.over) ? c.over : 0;
  const a = waiting > 0 ? `${waiting} waiting` : '';
  const b = a && over > 0 && isFiniteNumber(thresholdMin) ? ` · ${over} over ${thresholdMin} min` : '';
  return { a, b };
}

/** "Arrived 3 · Ready 2" (the Check-in column's subtitle and the page header). */
export function arrivedReadyLine(counts) {
  const c = counts && typeof counts === 'object' ? counts : {};
  return `Arrived ${isFiniteNumber(c.arrived) ? c.arrived : 0} · Ready ${isFiniteNumber(c.ready) ? c.ready : 0}`;
}

/** "Checked out 2 · Dismissed 1" (the Check-out column's subtitle). */
export function checkoutLine(counts) {
  const c = counts && typeof counts === 'object' ? counts : {};
  return `Checked out ${isFiniteNumber(c.checkedOut) ? c.checkedOut : 0} · Dismissed ${isFiniteNumber(c.dismissed) ? c.dismissed : 0}`;
}

/**
 * A room's subtitle: the provider with the patient in it; else "Open · left N min ago" when someone has left it
 * today; else "Open".
 */
export function roomSub(room, providerName, nowMs) {
  const r = room && typeof room === 'object' ? room : {};
  if (r.occupied) return text(providerName);
  if (isFiniteNumber(r.lastLeftMs) && isFiniteNumber(nowMs) && nowMs >= r.lastLeftMs) {
    return `Open · left ${Math.floor((nowMs - r.lastLeftMs) / MS_PER_MINUTE)} min ago`;
  }
  return 'Open';
}

// ── colours ──────────────────────────────────────────────────────────────────────────────────────

function hexToRgb(hex) {
  if (typeof hex !== 'string' || !/^#[0-9a-f]{6}$/i.test(hex)) return null;
  return [1, 3, 5].map((i) => parseInt(hex.slice(i, i + 2), 16));
}

/** `hex` mixed over `base` at strength k (0..1), as '#rrggbb'. Not a hex colour ⇒ `base`. */
export function tintHex(hex, k, base) {
  const fallback = typeof base === 'string' && hexToRgb(base) ? base : '#ffffff';
  const a = hexToRgb(hex);
  const b = hexToRgb(fallback);
  if (!a) return fallback;
  const strength = isFiniteNumber(k) ? Math.min(1, Math.max(0, k)) : 0;
  return `#${a.map((v, i) => Math.round(v * strength + b[i] * (1 - strength)).toString(16).padStart(2, '0')).join('')}`;
}

// ── pings ────────────────────────────────────────────────────────────────────────────────────────

/** "Just sent" / "Waiting 6 min" / "Waiting 1 hr 5 min", plus the tone the SAME threshold gives a card. */
export function pingElapsed(createdMs, nowMs, thresholdMin) {
  if (!isFiniteNumber(createdMs) || !isFiniteNumber(nowMs)) return { text: '', tone: 'n', minutes: null };
  const minutes = Math.max(0, Math.floor((nowMs - createdMs) / MS_PER_MINUTE));
  let label;
  if (minutes < 1) label = 'Just sent';
  else if (minutes >= 60) label = `Waiting ${Math.floor(minutes / 60)} hr ${minutes % 60} min`;
  else label = `Waiting ${minutes} min`;
  let tone = 'n';
  if (isFiniteNumber(thresholdMin) && thresholdMin > 0) {
    if (minutes >= 2 * thresholdMin) tone = 'r';
    else if (minutes >= thresholdMin) tone = 'a';
  }
  return { text: label, tone, minutes };
}

/** "Dr. Kapoor", "Priya", "Front" — the legend's short form of a team member's name. */
export function shortName(name) {
  const full = text(name);
  if (!full) return 'Everyone';
  if (/^Dr\.? /i.test(full)) {
    const words = full.split(/\s+/);
    return `Dr. ${words[words.length - 1]}`;
  }
  return full.replace(/\s+RDH$/i, '').split(/\s+/)[0];
}

/**
 * @param {Array} alerts  rows { index, createdMs, to, from, opName, opLabel, msg } — the page maps the document's
 *                        alerts into this (it owns the roster and the Firestore timestamp reader)
 * @param {{nowMs: number, thresholdMin: ?number, toName: Function, fromName: Function, colorOf: Function, tz: string}} ctx
 *   toName(employeeID) / fromName(employeeID) → a display name; colorOf(employeeID) → '#rrggbb' | null
 * @returns {{pings: Array, legend: Array, hasLegend: boolean}}
 */
export function buildPings(alerts, ctx) {
  const list = Array.isArray(alerts) ? alerts : [];
  const c = ctx && typeof ctx === 'object' ? ctx : {};
  const toName = typeof c.toName === 'function' ? c.toName : () => '';
  const fromName = typeof c.fromName === 'function' ? c.fromName : () => '';
  const colorOf = typeof c.colorOf === 'function' ? c.colorOf : () => null;

  const pings = list.map((a, position) => {
    const to = a && a.to !== null && a.to !== undefined && a.to !== '' ? String(a.to) : '';
    const recipient = text(toName(to));
    const senderFull = text(fromName(a && a.from));
    const sender = senderFull ? shortName(senderFull) : '';
    const color = to ? colorOf(to) : null;
    const elapsed = pingElapsed(a && a.createdMs, c.nowMs, c.thresholdMin);
    const op = text(a && a.opLabel) || text(a && a.opName) || 'A room';
    return {
      key: `ping-${position}-${to || 'all'}-${isFiniteNumber(a && a.createdMs) ? a.createdMs : position}`,
      index: a && isFiniteNumber(a.index) ? a.index : position,
      to: to || 'all',
      toName: recipient,
      op,
      msg: text(a && a.msg) || 'Needs you',
      from: `from ${sender || 'someone'}${recipient ? ` · to ${recipient}` : ''}`,
      at: clockText(a && a.createdMs, c.tz),
      elapsed,
      color: color || null,
    };
  });

  const legend = [];
  pings.forEach((p) => {
    let entry = legend.find((l) => l.to === p.to);
    if (!entry) {
      entry = { to: p.to, label: shortName(p.toName), color: p.color, n: 0 };
      legend.push(entry);
    }
    entry.n += 1;
  });

  return { pings, legend, hasLegend: legend.length > 1 || (legend.length === 1 && pings.length > 2) };
}

// ── chunk C: the Operatory facts, the Wall clock ────────────────────────────────────────────────

/** 'am' | 'pm' for an instant at the practice (the Wall's big clock prints "9:46" and a small "am"); '' when unusable. */
export function meridiemText(ms, tz) {
  const parts = partsOf(ms, tz, { hour: 'numeric', minute: '2-digit', hour12: true });
  if (!parts) return '';
  const period = part(parts, 'dayPeriod').toLowerCase().replace(/[^a-z]/g, '');
  return period === 'am' || period === 'pm' ? period : '';
}

/**
 * How a provider reads on the Operatory facts line: "Dr. Kapoor" for a doctor (Open Dental's provider Suffix is DDS /
 * DMD / MD / DO), else the name the page and the classic board already use ("First Last"). '' for nobody.
 * @param {{FName?: string, LName?: string, Suffix?: string}} provider
 */
export function providerLabel(provider) {
  const p = provider && typeof provider === 'object' ? provider : {};
  const first = text(p.FName);
  const last = text(p.LName);
  const suffix = text(p.Suffix).replace(/[.\s]/g, '').toUpperCase();
  if (last && /^(DDS|DMD|MD|DO)$/.test(suffix)) return `Dr. ${last}`;
  return [first, last].filter(Boolean).join(' ');
}

/** "Dr. Kapoor · seated 9:23 · booked for 9:15" — each part only when it is known. */
export function operatoryFacts(card, providerName, tz) {
  const c = card && typeof card === 'object' ? card : {};
  const parts = [];
  if (text(providerName)) parts.push(text(providerName));
  const seated = clockText(c.seatedMs, tz);
  if (seated) parts.push(`seated ${seated}`);
  const booked = clockText(c.scheduledMs, tz);
  if (booked) parts.push(`booked for ${booked}`);
  return parts.join(' · ');
}

/** The empty room's second line: "Last patient left 12 min ago", or "No one seated yet today". */
export function lastLeftText(room, nowMs) {
  const r = room && typeof room === 'object' ? room : {};
  if (isFiniteNumber(r.lastLeftMs) && isFiniteNumber(nowMs) && nowMs >= r.lastLeftMs) {
    return `Last patient left ${Math.floor((nowMs - r.lastLeftMs) / MS_PER_MINUTE)} min ago`;
  }
  return 'No one seated yet today';
}
